/**
 * MSW cart guards
 *
 * 1) Upsell guard — the half-price extra cap (extra-msw-cap-1) is only allowed in the cart
 *    when a Cap + Strap combo (msw-cap-strap) is present; its quantity is capped to the combo quantity.
 *
 * 1b) Cap swap (2026-10-22, docs/CART-CAP-AUTOSWAP-2026-10-22.md) — while there are fewer half-price caps than combos,
 *    a full-price Extra MSW Trucker / Dad Cap already in the cart is converted into the half-price cap of the same
 *    style and colour (newest line first, one unit at a time, never more than the open slots). Skips caps whose
 *    half-price variant is missing or sold out. The half-price cap is added first and the full-price unit removed only
 *    if that add worked, so a failed add never costs the shopper their cap. No message: the drawer shows it as
 *    "+ Extra MSW Cap" under its combo.
 *
 * 2) Free-strap gift — shoppers who arrive with the sign-up code (?discount=FREEMSWSTRAP, or the
 *    discount cookie Shopify sets from that URL) get the Sand Beige Extra Hook Strap added
 *    automatically whenever a combo is in the cart, tagged with the _free_gift line property
 *    (the drawer renders that as FREE). It's removed if the combo goes, re-added if the combo comes back.
 *    The code itself is applied to the cart as soon as the gift line is there (POST cart/update.js {discount}),
 *    so the drawer subtotal and the free-shipping bar count the strap at $0 (2026-10-20: before this the
 *    cart held it at full price until Checkout, and the bar promised free shipping at $73 / CA$100 that
 *    checkout then charged for). Other codes already on the cart are kept; the code comes off again when the
 *    gift is removed. At checkout the code is still re-applied via /discount/<CODE> as a fallback.
 *
 * window.mswCheckUpsell(opts) -> Promise<boolean>   resolves true if it changed the cart
 *   opts.refresh (default true): re-render the cart drawer after a change
 * window.mswHasFreeStrap() -> boolean
 * window.mswFreeStrapCode  -> 'FREEMSWSTRAP'
 */
(function() {
  'use strict';

  var COMBO_HANDLE  = 'msw-cap-strap';
  var UPSELL_HANDLE = 'extra-msw-cap-1';   // was extra-hook-strap-1 (the $11 strap, retired 2026-10-21)
  var GIFT_HANDLE   = 'extra-hook-strap';
  var FULL_CAP_STYLE = { 'extra-msw-trucker': 'Trucker', 'extra-msw-dad-cap': 'Dad Cap' };   // full-price caps -> half-price style
  var GIFT_COLOR    = 'Sand Beige';
  var GIFT_CODE     = 'FREEMSWSTRAP';
  var LS_KEY        = 'msw_free_strap';
  var inflight = null;
  var giftVariantId = null;

  window.mswFreeStrapCode = GIFT_CODE;

  /* ── code detection ─────────────────────────────────────────────────────────── */
  function detectCode() {
    try {
      var p = new URLSearchParams(window.location.search).get('discount');
      if (p && p.toUpperCase() === GIFT_CODE) { localStorage.setItem(LS_KEY, '1'); return; }
      var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      if (m && decodeURIComponent(m[1]).toUpperCase() === GIFT_CODE) localStorage.setItem(LS_KEY, '1');
    } catch (e) {}
  }
  function hasCode() { try { return localStorage.getItem(LS_KEY) === '1'; } catch (e) { return false; } }
  window.mswHasFreeStrap = hasCode;

  /* ── gift variant lookup (cached per session) ───────────────────────────────── */
  function getGiftVariantId() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    try { var c = sessionStorage.getItem('msw_gift_vid'); if (c) { giftVariantId = parseInt(c, 10); return Promise.resolve(giftVariantId); } } catch (e) {}
    return fetch(window.Shopify.routes.root + 'products/' + GIFT_HANDLE + '.js')
      .then(function(r) { return r.json(); })
      .then(function(p) {
        var v = (p.variants || []).find(function(x) { return x.available && String(x.option1).trim() === GIFT_COLOR; })
             || (p.variants || []).find(function(x) { return x.available; });
        if (!v) return null;
        giftVariantId = v.id;
        try { sessionStorage.setItem('msw_gift_vid', String(v.id)); } catch (e) {}
        return v.id;
      })
      .catch(function() { return null; });
  }

  /* ── half-price cap variants by "<Style> - <Color>" (fetched once per page, only when a swap is possible) ── */
  var halfCapMap = null;
  function getHalfCapMap() {
    if (halfCapMap) return Promise.resolve(halfCapMap);
    return fetch(window.Shopify.routes.root + 'products/' + UPSELL_HANDLE + '.js')
      .then(function(r) { return r.json(); })
      .then(function(p) {
        var map = {};
        (p.variants || []).forEach(function(v) { map[String(v.title).trim()] = { id: v.id, available: !!v.available }; });
        halfCapMap = map; return map;
      })
      .catch(function() { return null; });
  }
  function capColor(it) {
    var o = it.options_with_values && it.options_with_values[0];
    return String((o && o.value) || it.variant_title || '').trim();
  }

  /* ── the sign-up code on the cart itself (2026-10-20) ───────────────────────────
     cart.discount_codes = [{ code, applicable }]. If Shopify rejects the code, it is not retried on this page,
     so a rejected code can never loop the guard. `extra` adds request fields (e.g. sections to render in the same call). */
  var codeRejected = false;
  function cartCodes(cart) { return (cart.discount_codes || []).map(function(c) { return c.code; }); }
  function hasGiftCode(cart) {
    return (cart.discount_codes || []).some(function(c) { return String(c.code).toUpperCase() === GIFT_CODE && c.applicable; });
  }
  function setCodes(codes, extra) {
    var body = { discount: codes.join(',') };
    if (extra) Object.keys(extra).forEach(function(k) { body[k] = extra[k]; });
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }).then(function(r) { return r.json(); });
  }
  function applyGiftCode(cart, extra) {
    if (codeRejected || hasGiftCode(cart)) return Promise.resolve(null);
    var others = cartCodes(cart).filter(function(c) { return String(c).toUpperCase() !== GIFT_CODE; });
    return setCodes(others.concat([GIFT_CODE]), extra).then(function(res) {
      if (!res || !hasGiftCode(res)) codeRejected = true;
      return res;
    }).catch(function() { codeRejected = true; return null; });
  }
  function removeGiftCode(cart) {
    var codes = cartCodes(cart);
    if (!codes.some(function(c) { return String(c).toUpperCase() === GIFT_CODE; })) return Promise.resolve(null);
    return setCodes(codes.filter(function(c) { return String(c).toUpperCase() !== GIFT_CODE; }));
  }

  /* ── main check ─────────────────────────────────────────────────────────────── */
  function checkCart(opts) {
    opts = opts || {};
    var shouldRefresh = opts.refresh !== false;
    if (inflight) return inflight;

    inflight = fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        var items = cart.items || [];
        var comboQty = 0, upsell = [], giftLines = [];
        var fullCaps = [];   // full-price caps, in cart order (Shopify lists the newest line first)
        items.forEach(function(it) {
          if (FULL_CAP_STYLE[it.handle] && !(it.properties && it.properties._free_gift)) fullCaps.push({ key: it.key, qty: it.quantity, style: FULL_CAP_STYLE[it.handle], color: capColor(it) });
          if (it.handle === COMBO_HANDLE) comboQty += it.quantity;
          if (it.handle === UPSELL_HANDLE) upsell.push({ key: it.key, qty: it.quantity });
          if (it.handle === GIFT_HANDLE && it.properties && it.properties._free_gift) giftLines.push({ key: it.key, qty: it.quantity });
        });

        var updates = {};
        var changed = false;

        // (1) cap the upsell (half-price extra cap) to the combo count, trimming the last lines first
        var upsellQty = upsell.reduce(function(n, u) { return n + u.qty; }, 0);
        if (upsellQty > comboQty) {
          var excess = upsellQty - comboQty;
          for (var k = upsell.length - 1; k >= 0 && excess > 0; k--) {
            if (upsell[k].qty <= excess) { updates[upsell[k].key] = 0; excess -= upsell[k].qty; }
            else { updates[upsell[k].key] = upsell[k].qty - excess; excess = 0; }
          }
        }

        // (2) free strap: exactly one gift line while a combo is present, none otherwise
        var giftQty = giftLines.reduce(function(n, g) { return n + g.qty; }, 0);
        var wantGift = hasCode() && comboQty > 0;
        if (!wantGift && giftQty > 0) {
          giftLines.forEach(function(g) { updates[g.key] = 0; });
        } else if (wantGift && giftQty > 1) {
          giftLines.forEach(function(g, i) { updates[g.key] = i === 0 ? 1 : 0; });
        }

        var doUpdate = Object.keys(updates).length
          ? fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: updates })
            }).then(function() { changed = true; })
          : Promise.resolve();

        var giftInCart = wantGift && giftQty > 0;
        return doUpdate.then(function() {
          if (!(wantGift && giftQty === 0)) return;
          return getGiftVariantId().then(function(vid) {
            if (!vid) return;
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: { _free_gift: 'true' } }] })
            }).then(function(r) { if (r.ok) { changed = true; giftInCart = true; } });
          });
        }).then(function() {
          // (1b) open half-price slots go to full-price caps already in the cart (add first, remove only if the add worked)
          var openSlots = comboQty - upsellQty;
          if (openSlots <= 0 || !fullCaps.length) return;
          return getHalfCapMap().then(function(map) {
            if (!map) return;
            var adds = {}, removes = {}, left = openSlots;
            fullCaps.forEach(function(fc) {
              var hv = map[fc.style + ' - ' + fc.color];
              if (!hv || !hv.available) return;   // no half-price twin, or sold out: try the next cap
              var n = Math.min(fc.qty, left);
              if (n <= 0) return;
              adds[hv.id] = (adds[hv.id] || 0) + n;
              removes[fc.key] = fc.qty - n;
              left -= n;
            });
            var ids = Object.keys(adds);
            if (!ids.length) return;
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: ids.map(function(id) { return { id: parseInt(id, 10), quantity: adds[id], properties: { _src: 'cart-swap' } }; }) })
            }).then(function(r) {
              if (!r.ok) return;
              changed = true;
              return fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: removes })
              });
            });
          });
        }).then(function() {
          // (3) the code on the cart: on while the gift is in it, off when the gift was just removed
          if (giftInCart) return applyGiftCode(cart).then(function(res) { if (res) changed = true; });
          if (!wantGift && giftQty > 0) return removeGiftCode(cart).then(function(res) { if (res) changed = true; });
        }).then(function() {
          if (changed && shouldRefresh && typeof window.mswCartRefresh === 'function') window.mswCartRefresh();
          return changed;
        });
      })
      .catch(function(err) { console.error('MSW cart guard error:', err); return false; })
      .finally(function() { inflight = null; });

    return inflight;
  }

  window.mswCheckUpsell = checkCart;

  /* ── cart restore from ?cart=VARIANT:QTY,VARIANT:QTY (abandoned-cart emails) ─────
     Replaces the current cart with those lines (plus the free strap for code holders) and
     opens the drawer. The drawer opens immediately in a loading state; the network work
     (clear → single add.js that also returns the drawer HTML) runs while the page renders. */
  function restoreCartFromParam() {
    var raw;
    try { raw = new URLSearchParams(window.location.search).get('cart'); } catch (e) { return; }
    if (!raw) return;
    var items = raw.split(',').map(function(s) { return s.trim(); }).filter(Boolean).map(function(s) {
      var p = s.split(':'); return { id: parseInt(p[0], 10), quantity: Math.max(1, parseInt(p[1] || '1', 10) || 1) };
    }).filter(function(i) { return i.id > 0; });
    try { var u = new URL(window.location.href); u.searchParams.delete('cart'); window.history.replaceState({}, '', u.toString()); } catch (e) {}
    if (!items.length) return;

    var root = window.Shopify.routes.root;
    var drawerEl = document.querySelector('cart-drawer');

    // 1) open the drawer right away, in a loading state
    if (drawerEl) {
      drawerEl.classList.add('msw-loading');
      requestAnimationFrame(function() { drawerEl.classList.add('animate', 'active'); });
      document.body.classList.add('overflow-hidden', 'msw-drawer-open');
    }

    function applySections(data) {
      var sections = data && data.sections;
      if (!sections) return false;
      var d = sections['cart-drawer'] && new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      var target = document.querySelector('#CartDrawer');
      if (d && target) target.innerHTML = d.innerHTML;
      var b = sections['cart-icon-bubble'] && new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      var bubble = document.getElementById('cart-icon-bubble');
      if (b && bubble) bubble.innerHTML = b.innerHTML;
      return !!d;
    }
    function addItems(list) {
      return fetch(root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: list, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      }).then(function(r) { return r.json(); }).then(function(data) {
        // one unavailable variant fails the whole request — retry the rest one by one
        if (data && data.status && data.status !== 200 && list.length > 1) {
          return list.reduce(function(chain, it) {
            return chain.then(function(last) { return addItems([it]).then(function(d) { return (d && !d.status) ? d : last; }); });
          }, Promise.resolve(null));
        }
        return data;
      });
    }

    // 2) clear + gift lookup in parallel, then one add.js that also returns the drawer HTML
    var giftP = hasCode() ? getGiftVariantId() : Promise.resolve(null);
    Promise.all([fetch(root + 'cart/clear.js', { method: 'POST' }), giftP])
      .then(function(res) {
        var vid = res[1];
        if (vid) items.push({ id: vid, quantity: 1, properties: { _free_gift: 'true' } });
        return addItems(items);
      })
      .then(function(data) {
        // code holders: put the code on the rebuilt cart before drawing it, so the drawer renders once at the real total
        if (!hasCode() || !data || data.status) return data;
        return fetch(root + 'cart.js', { cache: 'no-store' }).then(function(r) { return r.json(); }).then(function(cart) {
          var hasGift = (cart.items || []).some(function(it) { return it.handle === GIFT_HANDLE && it.properties && it.properties._free_gift; });
          if (!hasGift) return data;
          return applyGiftCode(cart, { sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
            .then(function(res) { return (res && res.sections) ? res : data; });
        }).catch(function() { return data; });
      })
      .then(function(data) {
        var rendered = applySections(data);
        if (drawerEl) drawerEl.classList.remove('is-empty', 'msw-loading');
        if (rendered) {
          if (typeof window.cartUpsellInit === 'function') window.cartUpsellInit();
          if (typeof window.updateFreeShippingBar === 'function') window.updateFreeShippingBar();
          checkCart(); // safety net (e.g. gift without a combo) — re-renders only if it changes something
        } else if (typeof window.mswCartRefresh === 'function') {
          window.mswCartRefresh();
        }
      })
      .catch(function(err) {
        console.error('MSW cart restore error:', err);
        if (drawerEl) drawerEl.classList.remove('msw-loading');
        if (typeof window.mswCartRefresh === 'function') window.mswCartRefresh();
      });
  }

  /* ── product-page banner for code holders ───────────────────────────────────── */
  function showBanner() {
    if (!hasCode()) return;
    document.querySelectorAll('.msw-free-strap-banner').forEach(function(el) { el.style.display = 'flex'; });
  }

  detectCode();
  // ?cart= restore starts immediately (this script runs after the drawer markup, before the page finishes rendering)
  var restoring = false;
  try { restoring = !!new URLSearchParams(window.location.search).get('cart'); } catch (e) {}
  if (restoring) restoreCartFromParam();
  function boot() { showBanner(); if (!restoring) checkCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
